import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';
import './CreateProject.css';

interface CreateProjectProps {
  onClose: () => void;
  onSuccess: (projectId: number) => void;
  preselectedCommuneId?: number;
  preselectedCommuneName?: string;
}

const CreateProject: React.FC<CreateProjectProps> = ({ onClose, onSuccess, preselectedCommuneId, preselectedCommuneName }) => {
  const [name, setName] = useState('');
  const [object, setObject] = useState('');
  const [mandant, setMandant] = useState('');
  const [clientId, setClientId] = useState('');
  const [communeId, setCommuneId] = useState(preselectedCommuneId ? preselectedCommuneId.toString() : '');
  const [clients, setClients] = useState<any[]>([]);
  const [communes, setCommunes] = useState<any[]>([]);
  const [filteredCommunes, setFilteredCommunes] = useState<any[]>([]);
  const [communeSearch, setCommuneSearch] = useState(preselectedCommuneName || '');
  const [showCommuneDropdown, setShowCommuneDropdown] = useState(false);
  const [showNewClient, setShowNewClient] = useState(false);
  const [newClient, setNewClient] = useState({
    name: '',
    department: '',
    address: '',
    contact_person: ''
  });

  useEffect(() => {
    loadClients();
    loadCommunes();
  }, []);

  const loadClients = async () => {
    try {
      const response = await fetch(`${API_URL}/clients`);
      const data = await response.json();
      setClients(data);
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  const loadCommunes = async () => {
    try {
      const response = await fetch(`${API_URL}/communes`);
      const data = await response.json();
      setCommunes(data);
      setFilteredCommunes(data);
    } catch (error) {
      console.error('Error loading communes:', error);
    }
  };

  const handleCommuneSearch = (value: string) => {
    setCommuneSearch(value);
    const filtered = communes.filter(c => 
      c.name.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredCommunes(filtered);
    setShowCommuneDropdown(value.length > 0);
  };

  const selectCommune = (commune: any) => {
    setCommuneId(commune.id.toString());
    setCommuneSearch(commune.name);
    setShowCommuneDropdown(false);
  };

  const createClient = async () => {
    if (!newClient.name.trim()) return;

    try {
      const response = await fetch(`${API_URL}/clients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newClient),
      });

      if (response.ok) {
        const createdClient = await response.json();
        setClients([...clients, createdClient]);
        setClientId(createdClient.id.toString());
        setNewClient({ name: '', department: '', address: '', contact_person: '' });
        setShowNewClient(false);
      }
    } catch (error) {
      console.error('Error creating client:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name || !clientId || !communeId) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          object,
          mandant,
          client_id: parseInt(clientId),
          commune_id: parseInt(communeId),
        }),
      });

      if (response.ok) {
        const project = await response.json();
        onSuccess(project.id);
      }
    } catch (error) {
      console.error('Error creating project:', error);
    }
  };

  return (
    <div className="create-project-page">
      <div className="page-header">
        <h1>📁 Nouveau Projet</h1>
        <button type="button" onClick={onClose} className="btn-close">✕ Fermer</button>
      </div>

      <form onSubmit={handleSubmit} className="project-form">
          <div className="form-section">
            <h3>📋 Informations du Projet</h3>
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Mandat (Nom du projet) *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex: Établissement d'un plan quinquennal d'entretien du réseau routier communal 2012-2016"
                  required
                />
              </div>

              <div className="form-group full-width">
                <label>Objet (Routes à analyser)</label>
                <input
                  type="text"
                  value={object}
                  onChange={(e) => setObject(e.target.value)}
                  placeholder="Ex: Routes et chemins communaux"
                />
              </div>

              <div className="form-group">
                <label>Mandant (Personne responsable)</label>
                <input
                  type="text"
                  value={mandant}
                  onChange={(e) => setMandant(e.target.value)}
                  placeholder="Ex: M. Andrea NAME"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>👤 Client</h3>
            {!showNewClient ? (
              <div className="client-select-section">
                <div className="form-group">
                  <label>Sélectionner un client *</label>
                  <select
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    required
                  >
                    <option value="">-- Choisir un client --</option>
                    {clients.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <button type="button" onClick={() => setShowNewClient(true)} className="btn-new-client">
                  ➕ Créer un nouveau client
                </button>
              </div>
            ) : (
              <div className="new-client-section">
                <h4>Nouveau Client</h4>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Nom du client *</label>
                    <input
                      type="text"
                      value={newClient.name}
                      onChange={(e) => setNewClient({...newClient, name: e.target.value})}
                      placeholder="Ex: Commune de Corcelles-près-Payerne"
                    />
                  </div>
                  <div className="form-group">
                    <label>Service / Département</label>
                    <input
                      type="text"
                      value={newClient.department}
                      onChange={(e) => setNewClient({...newClient, department: e.target.value})}
                      placeholder="Ex: Service technique"
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>Adresse complète</label>
                    <textarea
                      value={newClient.address}
                      onChange={(e) => setNewClient({...newClient, address: e.target.value})}
                      placeholder="Ex: Rue du collège 5, 1562 Corcelles-près-Payerne"
                      rows={2}
                    />
                  </div>
                  <div className="form-group">
                    <label>Personne de contact</label>
                    <input
                      type="text"
                      value={newClient.contact_person}
                      onChange={(e) => setNewClient({...newClient, contact_person: e.target.value})}
                      placeholder="Ex: M. Andrea NAME"
                    />
                  </div>
                </div>
                <div className="form-actions">
                  <button type="button" onClick={createClient} className="btn-save">✓ Créer le client</button>
                  <button type="button" onClick={() => setShowNewClient(false)} className="btn-cancel">Annuler</button>
                </div>
              </div>
            )}
          </div>

          <div className="form-section">
            <h3>🏘️ Commune</h3>
            <div className="form-group">
              <label>Sélectionner la commune *</label>
              <div className="commune-search-wrapper">
                <input
                  type="text"
                  value={communeSearch}
                  onChange={(e) => handleCommuneSearch(e.target.value)}
                  onFocus={() => communeSearch && setShowCommuneDropdown(true)}
                  placeholder="Rechercher une commune..."
                  required
                  disabled={!!preselectedCommuneId}
                  style={preselectedCommuneId ? { background: '#e9ecef', cursor: 'not-allowed' } : {}}
                />
                {showCommuneDropdown && filteredCommunes.length > 0 && (
                  <div className="commune-dropdown">
                    {filteredCommunes.map(c => (
                      <div
                        key={c.id}
                        className="commune-option"
                        onClick={() => selectCommune(c)}
                      >
                        {c.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {preselectedCommuneId && (
                <small style={{ color: '#6c757d', marginTop: '5px' }}>✓ Commune présélectionnée</small>
              )}
            </div>
          </div>

        <div className="form-actions-bottom">
          <button type="button" onClick={onClose} className="btn-cancel-bottom">
            ✕ Annuler
          </button>
          <button type="submit" className="btn-submit-bottom">
            ✓ Créer le projet
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateProject;
